/**
 * lightGallery for the article page's image galleries.
 *
 * Markup comes from Elements/content/gallery.ctp and, on the `timeline`
 * layout, content/timeline-image-gallery.ctp. The thumbnail strip is a
 * separate plugin and must be passed explicitly — lightGallery does not load
 * plugins by itself.
 *
 * Unlike base_neo and faradeed_neo, lightGallery is IMPORTED here rather than
 * concatenated into frameworks.min.js. It has an ESM entry point, so bundling
 * it means tree-shaking and a working source map, and it stops being a global
 * that has to be registered in the right order.
 */
import lightGallery from 'lightgallery';
import lgThumbnail from 'lightgallery/plugins/thumbnail';
import { qsa } from './dom.js';

export function setupGallery() {
  const galleries = qsa('[data-gallery]');
  if (!galleries.length) return;

  for (const el of galleries) {
    // A magazine issue (Elements/content/pdf-pages.ctp, `data-gallery="pages"`)
    // gets NO thumbnail strip. The strip creates an <img> for every page the
    // moment the lightbox opens — 194 requests for a 194-page issue — and on a
    // new issue each one is a server-side Ghostscript render. Measured: one
    // click filled php-fpm to 46 of its 50 workers. The page grid behind the
    // lightbox already is the thumbnail view, so nothing is lost; without the
    // strip lightGallery loads only the open page and its neighbours.
    const isPages = el.dataset.gallery === 'pages';

    // A page that is not rendered yet can come back 503 when the server's
    // render slots are all busy (NewspaperimagesController::LOCK_WAIT). Retry
    // it a few times with a growing delay instead of leaving a broken card;
    // `error` does not bubble, hence the capture listener on the grid.
    if (isPages) {
      el.addEventListener(
        'error',
        (event) => {
          const img = event.target;
          if (!(img instanceof HTMLImageElement)) return;
          const tries = Number(img.dataset.retry || 0);
          if (tries >= 3) return;
          img.dataset.retry = String(tries + 1);
          setTimeout(
            () => {
              img.src = img.getAttribute('src');
            },
            3000 * (tries + 1)
          );
        },
        true
      );
    }

    lightGallery(el, {
      plugins: isPages ? [] : [lgThumbnail],
      selector: 'a',
      thumbnail: !isPages,
      // No `rtl` option here — this package version has none, and passing
      // one is silently ignored. The RTL positioning bug it would have
      // fixed (slide renders off-screen, counter reads backwards) is patched
      // in components.css instead — see "Gallery lightbox" there.
      download: false,
      counter: true,
      licenseKey: 'GPLv3',
      mobileSettings: { showCloseIcon: true, download: false },
    });
  }
}
